import * as AppConstants from "AppConstants";
import {
  getDateInFormat,
  startOfLocalDay,
  getLatestActivityDate,
  getEarliestActivityDate,
} from "utils/dateUtil";

/**
 * Row algebra and validation for bulk activity entry.
 *
 * A row is one ACTIVITY, not one day: several rows may carry the same date, because the schema has
 * always held more than one activity per owner per day and the point of the feature is a different
 * description per entry. Nothing here forbids a repeated date — the only date-wide rule is the
 * 24-hour roll-up in {@link dayRollUp}.
 *
 * @since NEXT_VERSION
 */

const PER_ENTRY_HOURS_MAX = AppConstants.MAXIMUM_ACTIVITY_HOURS_PER_DAY; // 12
const PER_DAY_HOURS_MAX = AppConstants.MAX_ACTIVITY_HOURS_PER_OWNER_PER_DAY; // 24

// @since NEXT_VERSION
export const BULK_ROW_MESSAGES = {
  date: {
    required: "Pick a date.",
    tooFarAhead: `The date cannot be more than ${AppConstants.ACTIVITY_FUTURE_DAYS_ALLOWED} days ahead.`,
    // @since NEXT_VERSION
    tooFarBack: `The date cannot be more than ${AppConstants.ACTIVITY_PAST_MONTHS_ALLOWED} months old.`,
  },
  hoursSpent: {
    required: "Required. Greater than 0, up to 12.",
    notNumber: "Enter a valid number.",
    tooLow: "Hours must be greater than 0.",
    tooHigh: `Hours cannot exceed ${PER_ENTRY_HOURS_MAX}.`,
    tooManyDecimals: "Enter at most one decimal place (e.g. 7.5).",
  },
  activityTypeId: { required: "Select an activity type." },
  description: {
    tooLong: `Description must be ${AppConstants.MAX_ACTIVITY_DESCRIPTION_LENGTH} characters or fewer.`,
  },
  batch: {
    empty: "Add at least one entry.",
    tooMany: `One dialog can hold at most ${AppConstants.MAX_ACTIVITY_ROWS_PER_DIALOG} entries.`,
  },
};

let rowSeq = 0;

/**
 * A blank row. `date` and `activityTypeId` are seeded from the caller — a new row lands on the same
 * day as the row it came from and inherits its activity type, so logging a run of similar work does
 * not mean re-picking them on every line. The very first row of a dialog gets neither, so a type is
 * always a deliberate choice at least once.
 *
 * Location is not a row field: it is chosen once for the whole batch and applied by
 * {@link toBulkPayload}.
 *
 * @since NEXT_VERSION
 */
export const makeRow = ({ date = null, activityTypeId = null } = {}) => {
  rowSeq += 1;
  return {
    id: `row-${rowSeq}`,
    date,
    clientTicketRef: "",
    description: "",
    activityTypeId,
    status: AppConstants.DEFAULT_ACTIVITY_STATUS,
    hoursSpent: "",
  };
};

/**
 * An empty row below `index`, inheriting that row's date and activity type. The ticket, description
 * and hours stay blank — those are the parts that genuinely differ per entry.
 *
 * @since NEXT_VERSION
 */
export const addRowAfter = (rows, index) => {
  const source = rows[index];
  const next = rows.slice();
  next.splice(
    index + 1,
    0,
    makeRow({ date: source?.date ?? null, activityTypeId: source?.activityTypeId ?? null })
  );
  return next;
};

/**
 * Clones a row onto the SAME date. Duplicate is how a second entry on one day gets made — which is
 * why the unique-date-per-row rule from the original mockup had to go.
 *
 * @since NEXT_VERSION
 */
export const duplicateRow = (rows, index) => {
  const source = rows[index];
  if (!source) return rows;
  rowSeq += 1;
  const next = rows.slice();
  next.splice(index + 1, 0, { ...source, id: `row-${rowSeq}` });
  return next;
};

/** Never empties the table: a dialog with no rows has nothing to submit. @since NEXT_VERSION */
export const removeRowAt = (rows, index) => {
  if (rows.length <= 1) return rows;
  const next = rows.slice();
  next.splice(index, 1);
  return next;
};

/**
 * Hours to one decimal, the only precision an entry accepts.
 *
 * <p>Summing them raw shows the error binary floating point cannot hold: 0.1 and 0.2 add up to
 * 0.30000000000000004, which is what a day total would otherwise read, and a day of exactly 24 h
 * can tip past the cap. @since NEXT_VERSION
 */
const roundToOneDecimal = (hours) => Math.round(hours * 10) / 10;

const hoursOf = (row) => {
  const parsed = typeof row.hoursSpent === "number" ? row.hoursSpent : parseFloat(row.hoursSpent);
  return Number.isNaN(parsed) ? 0 : parsed;
};

/**
 * One entry per distinct date, in first-seen order, carrying what this batch adds, what is already
 * saved, and whether the two together break the 24-hour ceiling.
 *
 * @param savedHoursByDate { "yyyy-MM-dd": hours } from loadDayHoursForOwner
 * @since NEXT_VERSION
 */
export const dayRollUp = (rows, savedHoursByDate = {}) => {
  const order = [];
  const byDate = {};

  rows.forEach((row) => {
    if (!row.date) return;
    if (!byDate[row.date]) {
      byDate[row.date] = { batchHours: 0, rowCount: 0 };
      order.push(row.date);
    }
    byDate[row.date].batchHours += hoursOf(row);
    byDate[row.date].rowCount += 1;
  });

  return order.map((date) => {
    const savedHours = roundToOneDecimal(Number(savedHoursByDate[date]) || 0);
    const batchHours = roundToOneDecimal(byDate[date].batchHours);
    const totalHours = roundToOneDecimal(savedHours + batchHours);
    return {
      date,
      batchHours,
      savedHours,
      totalHours,
      rowCount: byDate[date].rowCount,
      overCap: totalHours > PER_DAY_HOURS_MAX,
    };
  });
};

const validateDate = (date, earliestDate) => {
  if (!date) return BULK_ROW_MESSAGES.date.required;
  if (startOfLocalDay(date) > getLatestActivityDate()) return BULK_ROW_MESSAGES.date.tooFarAhead;
  // @since NEXT_VERSION
  if (startOfLocalDay(date) < earliestDate) return BULK_ROW_MESSAGES.date.tooFarBack;
  return null;
};

const validateHours = (raw) => {
  if (raw === "" || raw === null || raw === undefined) return BULK_ROW_MESSAGES.hoursSpent.required;
  const hrs = typeof raw === "number" ? raw : parseFloat(raw);
  if (Number.isNaN(hrs)) return BULK_ROW_MESSAGES.hoursSpent.notNumber;
  if (hrs <= 0) return BULK_ROW_MESSAGES.hoursSpent.tooLow;
  if (hrs > PER_ENTRY_HOURS_MAX) return BULK_ROW_MESSAGES.hoursSpent.tooHigh;
  if (Math.abs(Math.round(hrs * 10) - hrs * 10) > 1e-9) return BULK_ROW_MESSAGES.hoursSpent.tooManyDecimals;
  return null;
};

/**
 * Per-row errors plus one batch-level error, mirroring what the server enforces so a 422 is the
 * exception rather than the way the user finds out.
 *
 * A date over the cap marks EVERY row on that date, because no single row is at fault — the day is.
 *
 * @param options.earliestDate the oldest day a row may be dated. Defaults to the standard
 *   backdating window; the edit form relaxes it to the date the activity was already saved with,
 *   so a record older than the window stays correctable. @since NEXT_VERSION
 * @since NEXT_VERSION
 */
export const validateRows = (
  rows,
  savedHoursByDate = {},
  { earliestDate = getEarliestActivityDate() } = {}
) => {
  const rowErrors = rows.map((row) => {
    const errors = {};
    const date = validateDate(row.date, earliestDate);
    if (date) errors.date = date;
    const hours = validateHours(row.hoursSpent);
    if (hours) errors.hoursSpent = hours;
    if (row.activityTypeId === null || row.activityTypeId === undefined || row.activityTypeId === "") {
      errors.activityTypeId = BULK_ROW_MESSAGES.activityTypeId.required;
    }
    if (row.description && row.description.length > AppConstants.MAX_ACTIVITY_DESCRIPTION_LENGTH) {
      errors.description = BULK_ROW_MESSAGES.description.tooLong;
    }
    return errors;
  });

  dayRollUp(rows, savedHoursByDate)
    .filter((day) => day.overCap)
    .forEach((day) => {
      const message =
        `${day.date} would reach ${day.totalHours} h, over the ${PER_DAY_HOURS_MAX} h daily limit` +
        (day.savedHours > 0 ? ` (${day.savedHours} h already saved).` : ".");
      rows.forEach((row, index) => {
        if (row.date === day.date) rowErrors[index].hoursSpent = message;
      });
    });

  let batchError = null;
  if (rows.length === 0) batchError = BULK_ROW_MESSAGES.batch.empty;
  // The dialog's cap, not the endpoint's: it is the smaller of the two, so it is the one that
  // binds. @since NEXT_VERSION
  else if (rows.length > AppConstants.MAX_ACTIVITY_ROWS_PER_DIALOG) {
    batchError = BULK_ROW_MESSAGES.batch.tooMany;
  }

  return {
    isValid: batchError === null && rowErrors.every((errors) => Object.keys(errors).length === 0),
    rowErrors,
    batchError,
  };
};

const trimmed = (value) => (typeof value === "string" ? value.trim() : value);

/**
 * The request body the bulk endpoint expects. Fields are picked explicitly, so anything the dialog
 * hangs on a row for its own use never reaches the server.
 *
 * `location` is batch-level: one dialog logs one run of work, which is either onsite or offsite,
 * so it is chosen once above the table rather than per row.
 *
 * @since NEXT_VERSION
 */
export const toBulkPayload = ({
  ownerId,
  isMember,
  managerId = null,
  projectId = null,
  location = "Offsite",
  rows,
}) => ({
  // null rather than 0 for a manager-owned batch, matching what the single-save path sends; the
  // DTO's ownerId is a primitive long, which Jackson coerces from null, and the server ignores it
  // when isMember is false.
  ownerId: isMember ? ownerId : null,
  isMember,
  managerId: isMember ? null : managerId,
  projectId,
  entries: rows.map((row) => ({
    date: getDateInFormat(row.date),
    hoursSpent: typeof row.hoursSpent === "number" ? row.hoursSpent : parseFloat(row.hoursSpent),
    activityTypeId: row.activityTypeId,
    status: row.status,
    location,
    description: trimmed(row.description),
    clientTicketRef: trimmed(row.clientTicketRef),
  })),
});

const normaliseTypeName = (value) => String(value ?? "").trim().toLowerCase();

/**
 * Stamps each row with the display name of its current activity type.
 *
 * Called the instant the client changes, while the OLD client's type list is still in hand. The
 * name is the only thing that survives a client switch -- ids are per client -- so it is the bridge
 * remapRowTypes uses once the new list arrives.
 *
 * @since NEXT_VERSION
 */
export const captureTypeNames = (rows, activityTypes = []) => {
  const nameById = new Map(
    activityTypes.filter(Boolean).map((type) => [String(type.id), type.activityDesc])
  );
  return rows.map((row) => ({
    ...row,
    typeNameHint: row.activityTypeId == null ? null : nameById.get(String(row.activityTypeId)) ?? null,
  }));
};

/**
 * Re-points each row's activity type at the new client's equivalent, by name.
 *
 * A row whose captured name exists for the new client keeps its selection under the new id. A row
 * whose name has no counterpart is cleared -- and reported, so the user is told which rows need
 * attention instead of discovering it when the submit is refused. An id with no captured name is
 * cleared rather than trusted: it belongs to a client that is no longer selected.
 *
 * @returns {{rows: Array, kept: number, cleared: number, clearedRowNumbers: number[]}}
 * @since NEXT_VERSION
 */
export const remapRowTypes = (rows, activityTypes = []) => {
  const idByName = new Map(
    activityTypes.filter(Boolean).map((type) => [normaliseTypeName(type.activityDesc), type.id])
  );

  let kept = 0;
  let cleared = 0;
  const clearedRowNumbers = [];

  const availableIds = new Set(activityTypes.filter(Boolean).map((type) => String(type.id)));

  const next = rows.map((row, index) => {
    if (row.activityTypeId == null && !row.typeNameHint) {
      return { ...row, typeNameHint: null };
    }

    if (row.typeNameHint) {
      const match = idByName.get(normaliseTypeName(row.typeNameHint));
      if (match !== undefined) {
        kept += 1;
        return { ...row, activityTypeId: match, typeNameHint: null };
      }
      cleared += 1;
      clearedRowNumbers.push(index + 1);
      return { ...row, activityTypeId: null, typeNameHint: null };
    }

    // An id with no captured name was chosen AFTER the client switch was noticed but before the new
    // list arrived. Clearing it outright would throw away a pick the user just made, so it is judged
    // against the new client's list instead.
    if (availableIds.has(String(row.activityTypeId))) {
      kept += 1;
      return { ...row, typeNameHint: null };
    }
    cleared += 1;
    clearedRowNumbers.push(index + 1);
    return { ...row, activityTypeId: null, typeNameHint: null };
  });

  return { rows: next, kept, cleared, clearedRowNumbers };
};

/**
 * Clears any row activity type the current client does not offer.
 *
 * The backstop to {@link remapRowTypes}: that one runs on a client switch, this one on any other
 * arrival of a type list, so an id the client has retired since the row was filled in cannot be
 * posted.
 *
 * @since NEXT_VERSION
 */
export const dropUnavailableTypes = (rows, activityTypes = []) => {
  const available = new Set(activityTypes.filter(Boolean).map((type) => String(type.id)));
  let changed = false;
  const next = rows.map((row) => {
    if (row.activityTypeId == null) return row;
    if (available.has(String(row.activityTypeId))) return row;
    changed = true;
    return { ...row, activityTypeId: null };
  });
  return changed ? next : rows;
};
